import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../../../components/ui';
import { getThemeColors } from '../../../lib/theme';
import { PhoneLink } from '../../../components/PhoneLink';
import { v2FamilyAccounts, type FamilyMember } from '../../../lib/familyAccounts';
import { navigateGlobal, buildPath } from '../../../lib/route';
import { rowLinkProps } from '../../../lib/openLink';

/**
 * Reiter „Kontakte" — die übrigen Personen des Familienkontos.
 *
 * Quelle ist `GET /v2/admin/family-accounts/{id}` (members[]), nicht die
 * v1-Ansprechpartner aus dem Schnell-bearbeiten-Raster: gefragt sind die
 * weiteren Personen des Kontos (Angehörige, Rechnungsempfänger, Hausarzt …)
 * mit Name, Rolle und anwählbarer Telefonnummer.
 *
 * Die geöffnete Person selbst (`excludeUserId`) wird ausgeblendet — sie steht bereits im
 * Drawer-Kopf.
 */
export function KontakteTab({ accountId, excludeUserId, dark }: { accountId: number | null; excludeUserId?: number | null; dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [members, setMembers] = useState<FamilyMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (accountId == null) { setMembers([]); return () => { alive = false; }; }
    setMembers(null);
    setError(null);
    v2FamilyAccounts.get(accountId)
      .then(acc => { if (alive) setMembers(acc.members ?? []); })
      .catch(() => { if (alive) setError(tr('pflege.detail.kontakte_load_error')); });
    return () => { alive = false; };
  }, [accountId, tr]);

  if (accountId == null) {
    return <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('pflege.detail.kontakte_no_account')}</div>;
  }
  if (error) {
    return <div style={{ fontSize: 13, color: '#b42318', padding: '8px 0' }}>{error}</div>;
  }
  if (members === null) {
    return <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('common.loading')}</div>;
  }

  const others = members.filter(m => excludeUserId == null || m.user_id == null || m.user_id !== excludeUserId);

  if (others.length === 0) {
    return <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('pflege.detail.kontakte_empty')}</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {others.map(m => {
        const name = (m.fullname || [m.vorname, m.nachname].filter(Boolean).join(' ')).trim()
          || tr('pflege.detail.kontakte_unnamed');
        // Mobil hat Vorrang — im CRM ist das die Nummer, unter der die Person
        // tatsächlich erreichbar ist.
        const phone = m.mobile || m.telefon || null;
        // Batch-13 Paket E, seit 23.09.2026 auf die kanonische users.id-Form
        // umgestellt — jede Zeile verlinkt auf /pflege/{userId}/profildaten
        // ("profildaten" ist der tatsächliche PbTabId-Slug für "Profil" in
        // PbDetailPage). Kontaktpersonen ohne eigenen users-Datensatz (Alt-
        // Kontakte, m.user_id null) haben kein eigenes Profil zum Verlinken —
        // dann führt der Klick auf die Konto-Ansicht. Klick auf die
        // Telefonnummer bleibt eigenständig (stopPropagation), damit
        // "anrufen" nicht gleichzeitig navigiert.
        const openMember = () => (m.user_id != null
          ? navigateGlobal('pflege', String(m.user_id), 'profildaten')
          : navigateGlobal('pflege', 'konto', String(accountId)));
        const memberUrl = m.user_id != null
          ? buildPath('pflege', String(m.user_id), 'profildaten')
          : buildPath('pflege', 'konto', String(accountId));
        return (
          <div
            key={m.profile_id}
            role="button"
            tabIndex={0}
            {...rowLinkProps(memberUrl, openMember)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openMember(); } }}
            style={{
              border: `1px solid ${t.border}`, borderRadius: 10, padding: '10px 12px',
              display: 'flex', alignItems: 'center', gap: 12, background: t.card, cursor: 'pointer',
            }}
          >
            <Avatar name={name} userId={m.user_id ?? null} size={34} dark={dark} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{name}</div>
              <div style={{ fontSize: 11.5, color: t.muted, marginTop: 1 }}>
                {(m.profiltypen?.length ? m.profiltypen.join(', ') : m.profiltyp) || tr('pflege.detail.kontakte_no_role')}
                {m.familiengrad?.length ? ` · ${m.familiengrad.join(', ')}` : ''}
              </div>
            </div>
            <div style={{ fontSize: 12.5, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
              <PhoneLink number={phone} dark={dark} showIcon />
            </div>
          </div>
        );
      })}
    </div>
  );
}
